import { useQuery } from '@tanstack/react-query'
import cx from 'classnames'
import { MdClose } from 'react-icons/md'
import { animated, config, useSpring } from 'react-spring'

import http from '../../lib/http'
import { useAppDispatch, useAppSelector } from '../../store'
import { type Attachment, close } from '../../store/modules/previewer'

export function Previewer (): JSX.Element {
  let dispatch = useAppDispatch()

  let { isOpen, attachment } = useAppSelector(state => state.previewer)

  let buttonsAreOpen = isOpen && attachment?.mimeType.startsWith('image/')
  let pdfButtonsAreOpen = isOpen && attachment?.mimeType === 'application/pdf'

  let style = useSpring({
    opacity: isOpen ? 1 : 0
  })

  let pdfButtonsStyle = useSpring({
    opacity: pdfButtonsAreOpen
      ? 1
      : 0,

    transform: pdfButtonsAreOpen
      ? 'translateX(0px)'
      : 'translateX(-24px)',

    config: config.stiff
  })

  let buttonsStyle = useSpring({
    opacity: buttonsAreOpen
      ? 1
      : 0,

    transform: buttonsAreOpen
      ? 'translateX(0px)'
      : 'translateX(24px)',

    config: config.stiff
  })

  return (
    <div
      className='tailwind'
      style={{ position: 'relative', zIndex: 1000000000 }}
    >
      <div
        className={cx(
          'fixed top-0 left-0 h-screen z-[10000]',
          !isOpen && 'pointer-events-none'
        )}
        style={{
          width: '76vw'
        }}
      >
        <animated.div
          className={cx(
            'absolute top-0 left-0 h-14 rounded-r-md bg-[#333639] ~bg-red-400 shadow-md z-[10000] flex items-center justify-end gap-2 pr-3 will-change-transform',
            !pdfButtonsAreOpen && 'pointer-events-none'
          )}
          style={{
            ...pdfButtonsStyle,
            width: 'calc(100% + 48px)'
          }}
        >
          <button
            type='button'
            className='rounded-full p-2 bg-[#333639] hover:bg-[#434649] text-[#f1f1f1] flex items-center justify-center flex-shrink-0'
            onClick={() => {
              dispatch(close())
            }}
          >
            <MdClose
              size={16}
              className=''
            />
          </button>
        </animated.div>
      </div>

      <animated.div
        className={cx(
          'fixed flex flex-col top-0 left-0 h-screen overflow-hidden bg-black bg-opacity-80 backdrop-blur text-white z-[10000]',
          !isOpen && 'pointer-events-none'
        )}
        style={{
          ...style,
          width: '76vw'
        }}
      >

        {attachment?.mimeType === 'application/pdf' && (
          <PDFPreviewer
            attachment={attachment}
          />
        )}

        {attachment?.mimeType.startsWith('image/') && (
          <>
            <animated.div
              className='absolute top-[12px] right-4'
              style={buttonsStyle}
            >
              <button
                type='button'
                className='rounded-full p-2 bg-[transparent] hover:bg-[#434649] text-[#f1f1f1] flex items-center justify-center flex-shrink-0'
                onClick={() => {
                  dispatch(close())
                }}
              >
                <MdClose
                  size={16}
                  className=''
                />
              </button>
            </animated.div>

            <ImagePreviewer
              attachment={attachment}
            />
          </>
        )}
      </animated.div>
    </div>
  )
}

function PDFPreviewer ({ attachment }: { attachment: Attachment }): JSX.Element | null {
  invariant(attachment, 'Attachment must be defined')
  invariant(attachment.mimeType === 'application/pdf', 'Attachment must be a PDF')

  type QueryKey = [
    string,
    Attachment
  ]

  let query = useQuery<unknown, unknown, string, QueryKey>({
    queryKey: [
      'preview',
      attachment
    ],

    async queryFn ({ queryKey }) {
      let [ , attachment ] = queryKey
      if (attachment.id) {
        const response = await http.getRequest(`/attachments/${attachment.id}`, undefined, {
          responseType: 'blob'
        })
        const blob = new Blob([ response ], { type: attachment.mimeType })
        return URL.createObjectURL(blob)
      }
      if (attachment.data) {
        const base64 = attachment.data.startsWith('data:') ? attachment.data.split(',')[1] as string : attachment.data
        const byteCharacters = atob(base64)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        const file = new Blob([ byteArray ], { type: attachment.mimeType })
        return URL.createObjectURL(file)
      }
      return null
    },

    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchInterval: false
  })

  if (query.isLoading) {
    return (
      <div className='flex flex-col items-center justify-center w-full h-full'>
        <div className='animate-spin rounded-full h-32 w-32 border-b-2 border-white-900' />
      </div>
    )
  }

  if (!query.data) {
    return (
      <div className='flex flex-col items-center justify-center w-full h-full p-12'>
        <p className='text-center font-inter text-lg'>
          Something went wrong while loading the preview. Please try again later.
        </p>
      </div>
    )
  }

  return (
    <embed
      src={query.data}
      title={attachment.name}
      type={attachment.mimeType}
      className='w-full flex-grow'
    />
  )
}

function ImagePreviewer ({ attachment }: { attachment: Attachment }): JSX.Element | null {
  invariant(attachment, 'Attachment must be defined')
  invariant(attachment.mimeType.startsWith('image/'), 'Attachment must be an image')

  type QueryKey = [
    string,
    Attachment
  ]

  let query = useQuery<unknown, unknown, string, QueryKey>({
    queryKey: [
      'preview',
      attachment
    ],

    async queryFn ({ queryKey }) {
      let [ , attachment ] = queryKey
      if (attachment.id) {
        const response = await http.getRequest(`/attachments/${attachment.id}`, undefined, {
          responseType: 'blob'
        })
        const blob = new Blob([ response ], { type: attachment.mimeType })
        return URL.createObjectURL(blob)
      }
      if (attachment.data) {
        const base64 = attachment.data.startsWith('data:') ? attachment.data.split(',')[1] as string : attachment.data
        const byteCharacters = atob(base64)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        const file = new Blob([ byteArray ], { type: attachment.mimeType })
        return URL.createObjectURL(file)
      }
      return null
    },

    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchInterval: false
  })

  if (query.isLoading) {
    return (
      <div className='flex flex-col items-center justify-center w-full h-full'>
        <div className='animate-spin rounded-full h-32 w-32 border-b-2 border-white-900' />
      </div>
    )
  }

  if (!query.data) {
    return (
      <div className='flex flex-col items-center justify-center w-full h-full p-12'>
        <p className='text-center font-inter text-lg'>
          Something went wrong while loading the preview. Please try again later.
        </p>
      </div>
    )
  }

  return (
    <div className='p-8 flex items-center justify-center h-full'>
      <img
        src={query.data}
        alt={attachment.name}
        className='object-contain max-h-full max-w-full'
      />
    </div>
  )
}

function invariant (condition: unknown, message: string): asserts condition {
  if (!condition) {
    throw new Error(message)
  }
}
